import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';

import clsx from 'clsx';

import useFormatDateTime from '@/utils/hooks/format/useFormatDateTime';
import { useTransformStringOnOffset } from '@/utils/hooks/format/useTransformString';
import useAwsTranslate from '@/utils/hooks/language/useAwsTranslate';

import { post } from '@/utils/client';
// Utils
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';
import { getIGUsernameLink } from '@/utils/text/getIGUsernameTags';
import useIntersectionObserver from '@/utils/hooks/utils/useIntersectionObserver';
// Hooks
import useToggle from '@/utils/hooks/utils/useToggle';

import PropTypes from 'prop-types';

// Lib
import { MEDIA_TYPES } from '@/lib/medias/mediaTypes';
// Routes
import { COMMENT_REPLY } from '@/lib/routes/apiEndpoints';
import { FACEBOOK, INSTAGRAM } from '@/lib/socialProfile';

import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
// Components
import { avatarCustomColorClasses } from '@/components/base/Avatar/avatarConfig';
import Icon from '@/components/base/Icon';
import Loader from '@/components/base/Loader';
const EmojiPicker = dynamic(() => import('@/components/base/EmojiPicker'));
import AwsTranslateSameLanguageBanner from '@/components/composite/Banners/AwsTranslateSameLanguageBanner';
import CommentInput from '@/components/composite/Comments/CommentInput';

const btnClasses =
    'text-icoGray-400 text-xs hover:text-black dark:text-icoDarkMode-wolf dark:hover:text-white dark:hover:opacity-100';

const DEFAULT_DISPLAY_OPTIONS = {
    displayReply: true,
    displayDelete: true,
    displayReplies: true
};

const Comment = ({
    socialProfile,
    comment,
    user,
    activeFormReply = null,
    handleActiveFormReply = () => {
        /** */
    },
    handleReplyToAdd = () => {
        /** */
    },
    deleteComment = () => {
        /** */
    },
    markAsRead = () => {
        /** */
    },
    displayPost = false,
    onClickOnPost,
    className,
    replies,
    displayOptions = DEFAULT_DISPLAY_OPTIONS,
    replyEndpoint = COMMENT_REPLY,
    replyParams = undefined,
    relatedComments = [],
    isWinningComments = false,
    variant = 'default'
}) => {
    const displayConfig = { ...DEFAULT_DISPLAY_OPTIONS, ...displayOptions };

    const { t } = useTranslation('errors');
    const [markAsReadState, setMarkAsReadState] = useState({
        isSubmitting: false,
        error: false
    });
    const { formatTextWithComponent, formatFacebookText } = useTransformStringOnOffset();
    const commentRef = useRef();
    const commentInViewPort = useIntersectionObserver(commentRef, '0px');

    const { awsTranslation, awsToggleTranslation } = useAwsTranslate(
        socialProfile?.social_network_type === FACEBOOK
            ? formatFacebookText(comment?.data?.message, comment?.tags)
            : formatTextWithComponent(comment?.data?.message, comment?.tags)
    );

    const { isOpen: isOpenAwsTSLBanner, toggle: toggleAwsTSLBanner } = useToggle();

    useEffect(() => {
        async function launchMarkAsRead() {
            const { success } = await markAsRead(comment?.id, comment?.media_id);

            setMarkAsReadState({
                isSubmitting: false,
                error: !success
            });
        }

        let timer = null;

        if (
            !comment?.is_read &&
            commentInViewPort &&
            !markAsReadState?.isSubmitting &&
            !markAsReadState?.error
        ) {
            setMarkAsReadState({
                isSubmitting: true,
                error: null
            });
            timer = setTimeout(() => {
                launchMarkAsRead();
            }, 2000);
        }

        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commentInViewPort]);

    const mergedReplyParams = replyParams
        ? {
              SPID: socialProfile?.id,
              ...replyParams
          }
        : {
              SPID: socialProfile?.id,
              mediaID: comment?.media_id,
              commentID: activeFormReply?.parent?.id
          };

    return (
        <Comment.Container className={className} commentRef={commentRef} isRead={comment?.is_read}>
            <Comment.Post
                media={comment?.media}
                displayPost={displayPost}
                onClick={() => {
                    onClickOnPost(comment?.media?.id);
                }}
            />
            <div className="flex-1">
                <div
                    className={clsx('relative', {
                        'pr-5': variant === 'default',
                        'flex flex-col gap-4': variant === 'socialInbox'
                    })}>
                    {!comment?.is_read && variant === 'default' ? (
                        <span className="absolute right-2.5 top-1/2 z-1 h-1.5 w-1.5 -translate-y-1/2 transform rounded-full bg-icoBlue" />
                    ) : null}
                    <Comment.PostStatus
                        comment={comment}
                        displayPost={displayPost}
                        socialNetworkType={socialProfile?.social_network_type}
                    />

                    <div
                        className={clsx('flex w-full items-center gap-2', {
                            'mb-1': variant === 'default'
                        })}>
                        <Comment.Header
                            socialProfile={socialProfile}
                            comment={comment}
                            isWinningComments={isWinningComments}
                        />

                        {variant === 'socialInbox' && (
                            <div className="flex flex-1 items-center justify-between gap-4">
                                <Comment.Date
                                    created={comment?.created}
                                    user={user}
                                    className="text-xxs uppercase tracking-wider text-icoGray-500"
                                    showFullDate
                                />

                                <div className="flex items-center gap-3">
                                    {displayConfig.displayReply && (
                                        <Comment.ReplyButton
                                            handleActiveFormReply={handleActiveFormReply}
                                            isIcon
                                        />
                                    )}
                                    <Comment.TranslateButton
                                        awsTranslation={awsTranslation}
                                        awsToggleTranslation={awsToggleTranslation}
                                        isOpenAwsTSLBanner={isOpenAwsTSLBanner}
                                        toggleAwsTSLBanner={toggleAwsTSLBanner}
                                        isIcon
                                    />
                                    {displayConfig.displayDelete && (
                                        <Comment.DeleteButton
                                            deleteComment={deleteComment}
                                            isIcon
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <Comment.Message
                        awsTranslation={awsTranslation}
                        variant={variant}
                        tags={comment?.tags}
                    />

                    {variant === 'default' && (
                        <Comment.FooterWrapper>
                            <Comment.Date created={comment?.created} user={user} />
                            <Comment.TranslateButton
                                awsTranslation={awsTranslation}
                                awsToggleTranslation={awsToggleTranslation}
                                isOpenAwsTSLBanner={isOpenAwsTSLBanner}
                                toggleAwsTSLBanner={toggleAwsTSLBanner}
                            />
                            {displayConfig.displayReply && (
                                <Comment.ReplyButton
                                    handleActiveFormReply={handleActiveFormReply}
                                />
                            )}
                            {displayConfig.displayDelete && (
                                <Comment.DeleteButton deleteComment={deleteComment} />
                            )}
                        </Comment.FooterWrapper>
                    )}
                </div>
                <Comment.ReplyForm
                    socialProfile={socialProfile}
                    comment={comment}
                    handleReplyToAdd={handleReplyToAdd}
                    activeFormReply={activeFormReply}
                    handleActiveFormReply={handleActiveFormReply}
                    replyEndpoint={injectEndpointUrlParams(replyEndpoint, mergedReplyParams)}
                    relatedComments={relatedComments}
                />
                {markAsReadState?.error ? (
                    <p className="mt-1 font-GMedium text-xs text-icoRed-400">
                        {t('error_occurred_mark_as_read_comment')}
                    </p>
                ) : null}
                {isOpenAwsTSLBanner && (
                    <AwsTranslateSameLanguageBanner className="mt-3" onClose={toggleAwsTSLBanner} />
                )}
                {displayConfig?.displayReplies && replies}
            </div>
        </Comment.Container>
    );
};

Comment.propTypes = {
    socialProfile: PropTypes.object,
    activeFormReply: PropTypes.object,
    handleActiveFormReply: PropTypes.func,
    replies: PropTypes.node,
    comment: PropTypes.object,
    user: PropTypes.object,
    handleReplyToComment: PropTypes.func,
    deleteComment: PropTypes.func,
    displayPost: PropTypes.bool,
    className: PropTypes.string,
    onClickOnPost: PropTypes.func,
    isWinningComments: PropTypes.bool,
    variant: PropTypes.oneOf(['default', 'socialInbox'])
};

/**
 * Comment Container
 */

const Container = ({ children, className, commentRef, isRead, isWinner }) => {
    return (
        <div
            className={clsx(
                'relative flex rounded-md transition-colors duration-300',
                {
                    'bg-white dark:bg-icoDarkMode-anthracite': isRead,
                    'bg-icoBlue-100 dark:bg-icoBlue/20': !isRead
                },
                className
            )}
            ref={commentRef}>
            {isWinner && (
                <div className="absolute -right-3 -top-3 z-10 flex h-5 w-5 min-w-min items-center justify-center rounded-full bg-icoBlue">
                    <Icon icon="trophy" className="text-xs text-white" />
                </div>
            )}
            {children}
        </div>
    );
};

Comment.Container = Container;

/**
 * Comment Header
 */

const Header = ({ socialProfile, comment, isRead, isWinningComments = false }) => {
    const commentUserSlug =
        socialProfile?.social_network_type === FACEBOOK
            ? comment?.data?.from?.name
            : comment?.data?.username;
    const isActiveSocialProfile = socialProfile?.slug === commentUserSlug;

    return (
        <div className="flex items-center">
            <Avatar
                size="xs"
                url={isActiveSocialProfile ? socialProfile?.external_api_data?.avatar : null}
                fallback={commentUserSlug ? commentUserSlug.charAt(0).toUpperCase() : ''}
                customColorClass={
                    !isActiveSocialProfile && commentUserSlug
                        ? avatarCustomColorClasses[
                              commentUserSlug?.length % avatarCustomColorClasses?.length
                          ]
                        : ''
                }
            />
            {socialProfile?.social_network_type === INSTAGRAM ? (
                <a
                    href={getIGUsernameLink(commentUserSlug)}
                    className={clsx('ml-2 text-sm hover:text-icoBlue', {
                        'text-icoBlue': !(comment?.is_read || isRead)
                    })}
                    target="_blank"
                    rel="noopener noreferrer">
                    {commentUserSlug}
                </a>
            ) : (
                <p
                    className={clsx('ml-2 text-sm', {
                        'text-icoBlue': !(comment?.is_read || isRead)
                    })}>
                    {commentUserSlug}
                </p>
            )}

            {isWinningComments ? (
                <div className="ml-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-icoBlue text-xxs text-white">
                    <Icon icon="trophy" />
                </div>
            ) : null}
        </div>
    );
};

Comment.Header = Header;

/**
 * Comment Message
 */

const Message = ({ awsTranslation, message = '', variant = 'default', tags, className }) => {
    const { OffsetReplace } = useTransformStringOnOffset();

    let text = message;

    if (awsTranslation) {
        text = awsTranslation?.isDisplayed ? awsTranslation?.translated : awsTranslation?.original;
    }

    return (
        <div
            className={clsx('font-GMedium text-sm transition-opacity', className, {
                'opacity-50': awsTranslation?.isLoading,
                'leading-5': variant === 'default'
            })}>
            <OffsetReplace
                text={text}
                components={{
                    br: <br />,
                    ...Object.assign(
                        // eslint-disable-next-line jsx-a11y/anchor-has-content
                        {},
                        tags?.map((tag, id) => (
                            // eslint-disable-next-line jsx-a11y/anchor-has-content
                            <a
                                key={id}
                                href={tag.link}
                                className="text-icoBlue"
                                target="_blank"
                                rel="noopener noreferrer"
                            />
                        ))
                    )
                }}
            />
        </div>
    );
};

Comment.Message = Message;

/**
 * Comment Footer
 */
const FooterWrapper = (props) => {
    return <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1" {...props} />;
};

Comment.FooterWrapper = FooterWrapper;

const Date = ({ created, user, showFullDate, className }) => {
    const { formatFromNow, formatDateTimeNoTz } = useFormatDateTime(
        user?.settings?.timezone,
        user?.settings?.platform_language
    );
    return (
        <span
            className={clsx(
                'whitespace-nowrap font-GMedium text-xs text-icoGray-400 dark:text-icoDarkMode-wolf',
                className
            )}>
            {showFullDate
                ? formatDateTimeNoTz(created, 'weekDay_day_month_year_hour_minute')
                : formatFromNow(created)}
        </span>
    );
};

Comment.Date = Date;

const TranslateButton = ({
    awsTranslation,
    awsToggleTranslation,
    isOpenAwsTSLBanner,
    toggleAwsTSLBanner,
    isIcon = false
}) => {
    const { t } = useTranslation('commons');

    useEffect(() => {
        if (awsTranslation?.isSameLanguage && !isOpenAwsTSLBanner) {
            toggleAwsTSLBanner();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [awsTranslation]);

    const renderButtonContent = () => {
        if (isIcon) {
            return <Icon icon="translate" className="text-base tracking-wider text-icoGray-500" />;
        }

        return t(awsTranslation?.isDisplayed ? 'original_text' : 'translate');
    };

    return (
        <button
            className={clsx(btnClasses, {
                'cursor-wait': awsTranslation?.isLoading
            })}
            disabled={awsTranslation?.isLoading}
            onClick={() => {
                if (!awsTranslation?.isLoading) {
                    awsToggleTranslation();
                }
            }}
            data-testid="translate-caption-btn">
            {renderButtonContent()}
        </button>
    );
};

Comment.TranslateButton = TranslateButton;

const ReplyButton = ({ handleActiveFormReply, label, isIcon = false }) => {
    const { t } = useTranslation('commons');
    return (
        <button className={btnClasses} onClick={() => handleActiveFormReply('open')}>
            {isIcon ? (
                <Icon icon="reply" className="text-base tracking-wider text-icoGray-500" />
            ) : (
                label || t('reply')
            )}
        </button>
    );
};

Comment.ReplyButton = ReplyButton;

const ShowMoreButton = ({ onShowMore, showMore }) => {
    const { t } = useTranslation('commons');
    return (
        <button className={`flex space-x-1 align-bottom ${btnClasses}`} onClick={onShowMore}>
            <p>{t(`show_${showMore ? 'less' : 'more'}`)}</p>
            <Icon
                className={clsx('duration-300', {
                    'rotate-180': showMore
                })}
                icon="chevron-bottom"
            />
        </button>
    );
};

Comment.ShowMoreButton = ShowMoreButton;

const DeleteButton = ({ deleteComment, isIcon = false }) => {
    const { t } = useTranslation('commons');
    return (
        <button className={btnClasses} onClick={deleteComment}>
            {isIcon ? (
                <Icon icon="trash" className="text-base tracking-wider text-icoGray-500" />
            ) : (
                t('delete')
            )}
        </button>
    );
};

Comment.DeleteButton = DeleteButton;

/**
 * Form to reply a comment
 */
const ReplyForm = ({
    socialProfile,
    comment,
    activeFormReply,
    emojiPortalContainer,
    handleActiveFormReply,
    handleReplyToAdd,
    replyEndpoint,
    relatedComments
}) => {
    const { t } = useTranslation('commons');
    const [cursorPosition, setCursorPosition] = useState(0);
    const [mentions, setMentions] = useState([]);
    const commentInputRef = useRef(null);
    const {
        control,
        watch,
        handleSubmit,
        resetField,
        setValue,
        getValues,
        setError,
        formState: { errors, isSubmitting }
    } = useForm();

    const messageFieldKey = 'message';

    useEffect(() => {
        if (comment?.id === activeFormReply?.currentCommentId) {
            commentInputRef?.current?.focus();
            if (socialProfile?.social_network_type !== FACEBOOK) {
                setValue(messageFieldKey, `@${comment?.data?.username} `);
            }
            commentInputRef?.current?.focus();
            // Force carret to be at the end
            const tempValue = commentInputRef?.current?.value;
            commentInputRef.current.value = '';
            commentInputRef.current.value = tempValue;
        }
    }, [
        socialProfile?.social_network_type,
        comment?.id,
        activeFormReply?.currentCommentId,
        comment?.data?.from?.name,
        comment?.data?.username,
        setValue
    ]);

    if (comment?.id !== activeFormReply?.currentCommentId) {
        return null;
    }

    const onSubmit = async ({ message }) => {
        let messageFormatted = message;
        if (socialProfile?.social_network_type === FACEBOOK) {
            mentions.map((mention) => {
                messageFormatted = messageFormatted.replace(
                    `@[${mention.id}][${mention.display}]`,
                    `@[${mention.id}]`
                );
            });
        } else {
            mentions.map((mention) => {
                messageFormatted = messageFormatted.replace(
                    `@[${mention.id}][${mention.display.substring(1)}]`,
                    `${mention.display}`
                );
            });
        }

        try {
            const { data: reply } = await post(replyEndpoint, { message: messageFormatted });
            handleReplyToAdd(reply);
            handleActiveFormReply('close');
            resetField(messageFieldKey);
        } catch (e) {
            if (e.status === 422 && e.response?.data?.errors) {
                const errorsFields = e.response.data.errors;
                if (errorsFields) {
                    Object.keys(errorsFields).map((field) => {
                        const error_type = errorsFields[field][0];
                        // Display error under each field (only one here, the message field)
                        setError(field, {
                            type: 'manual',
                            message: t('errors:error_' + field + '.' + error_type)
                        });
                    });
                }
            } else {
                setError(messageFieldKey, {
                    type: 'manual',
                    message: t('errors:error_message.default')
                });
            }
        }
    };

    const handleClose = () => {
        resetField(messageFieldKey);
        handleActiveFormReply('close');
    };

    const message = watch(messageFieldKey);

    return (
        <Comment.ReplyFormContent
            socialProfile={socialProfile}
            cursorPosition={cursorPosition}
            emojiPortalContainer={emojiPortalContainer}
            errors={errors}
            isSubmitting={isSubmitting}
            getValues={getValues}
            handleSubmit={handleSubmit}
            message={message}
            messageFieldKey={messageFieldKey}
            onClose={handleClose}
            onSubmit={onSubmit}
            control={control}
            setCursorPosition={setCursorPosition}
            mentions={mentions}
            setMentions={setMentions}
            setValue={setValue}
            commentInputRef={commentInputRef}
            relatedComments={relatedComments}
        />
    );
};

ReplyForm.propTypes = {
    socialProfile: PropTypes.object.isRequired,
    comment: PropTypes.object.isRequired,
    replyToComment: PropTypes.object,
    handleReplyToComment: PropTypes.func
};

Comment.ReplyForm = ReplyForm;

const ReplyFormContent = ({
    socialProfile,
    cursorPosition,
    emojiPortalContainer,
    errors,
    isSubmitting,
    getValues,
    onClose,
    handleSubmit,
    message,
    messageFieldKey = 'message',
    onSubmit,
    control,
    setCursorPosition,
    mentions,
    setMentions,
    setValue,
    commentInputRef,
    relatedComments
}) => {
    const { t } = useTranslation('commons');
    const addEmojiInMessage = (emoji) => {
        const initialMessage = getValues(messageFieldKey);
        const newMessage = `${initialMessage?.substring(0, cursorPosition)}${
            emoji.native
        }${initialMessage?.substring(cursorPosition)}`;
        setValue(messageFieldKey, newMessage);
        setCursorPosition(cursorPosition + newMessage?.length - initialMessage?.length);
    };

    return (
        <>
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="relative mt-2.5 flex items-center rounded-md bg-icoGray-300 pr-5 dark:bg-icoDarkMode-deepBlack">
                <div className="flex-1 py-4 pr-3">
                    <Controller
                        control={control}
                        name={messageFieldKey}
                        rules={{
                            required: true
                        }}
                        render={({ field: { onChange, value } }) => (
                            <CommentInput
                                socialProfile={socialProfile}
                                value={value}
                                onChange={onChange}
                                mentions={mentions}
                                setMentions={setMentions}
                                setCursorPosition={setCursorPosition}
                                disabled={isSubmitting}
                                comments={relatedComments}
                                submitAction={handleSubmit(onSubmit)}
                                inputRef={commentInputRef}
                            />
                        )}
                    />
                </div>
                <EmojiPicker
                    portalContainer={emojiPortalContainer}
                    selectEmoji={addEmojiInMessage}
                    className="text-md mr-3"
                />
                {isSubmitting ? (
                    <Loader className="h-4 w-4 text-icoBlue" />
                ) : (
                    <button
                        type="submit"
                        data-testid="reply-btn"
                        disabled={!message || message?.length <= 0}
                        className={clsx('text-md flex items-center', {
                            'text-icoGray-400': !message || message?.length <= 0,
                            'text-icoBlue': message?.length > 0
                        })}>
                        <Icon icon="reply" />
                    </button>
                )}
                <button
                    aria-label={t('close')}
                    type="button"
                    className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center overflow-hidden rounded-full bg-icoGray-500 text-xs text-white focus:outline-none"
                    onClick={onClose}>
                    <Icon icon="close" />
                    <p className="absolute text-transparent">{t('close')}</p>
                </button>
            </form>
            {errors?.message && (
                <p className="mt-1 font-GMedium text-xs text-icoRed-400">
                    {errors?.message?.message}
                </p>
            )}
        </>
    );
};

ReplyFormContent.propTypes = {
    socialProfile: PropTypes.object.isRequired,
    cursorPosition: PropTypes.number.isRequired,
    errors: PropTypes.object.isRequired,
    isSubmiting: PropTypes.bool,
    getValues: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    message: PropTypes.string,
    messageFieldKey: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    setCursorPosition: PropTypes.func.isRequired,
    mentions: PropTypes.array,
    setMentions: PropTypes.func,
    setValue: PropTypes.func.isRequired
};

Comment.ReplyFormContent = ReplyFormContent;

const Post = ({ displayPost, media, onClick, remoteThumbnail }) =>
    displayPost ? (
        <div className="pr-5">
            <button
                className={clsx(
                    'block h-14 w-14 overflow-hidden rounded-sm bg-icoGray-100 dark:bg-icoDarkMode-deepBlack',
                    {
                        'cursor-default': !onClick
                    }
                )}
                onClick={onClick}>
                {media && media?.medias?.length > 0 ? (
                    <Img
                        postId={media?.id}
                        remoteThumbnail={remoteThumbnail}
                        src={
                            media?.medias?.[0]?.type === MEDIA_TYPES.TYPE_VIDEO ||
                            media?.medias?.[0]?.type === MEDIA_TYPES.TYPE_REEL
                                ? media?.medias?.[0]?.thumbnail
                                : media?.medias?.[0]?.image
                        }
                    />
                ) : (
                    <span className="flex h-full w-full items-center justify-center bg-icoOrange-100 text-2xl text-icoOrange-400">
                        <Icon icon="text" />
                    </span>
                )}
            </button>
        </div>
    ) : null;

Comment.Post = Post;

Post.propTypes = {
    media: PropTypes.object,
    displayPost: PropTypes.bool,
    onClick: PropTypes.func,
    remoteThumbnail: PropTypes.bool
};

/**
 * Image with error handler
 */

const Img = ({ postId, remoteThumbnail, src }) => {
    const [errorImg, setErrorImage] = useState(false);

    if (errorImg || !src) {
        return (
            <span className="flex h-full w-full items-center justify-center bg-icoGray-300 dark:bg-icoDarkMode-deepBlack">
                {/* Change Icon to real fallback */}
                <Icon
                    icon="content"
                    className="text-2xl text-icoGray-500 dark:text-icoDarkMode-wolf"
                />
            </span>
        );
    }

    return (
        <span className="relative block h-full w-full overflow-hidden">
            {remoteThumbnail ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                    src={src}
                    alt={postId}
                    className="h-full w-full object-cover"
                    onError={() => setErrorImage(true)}
                />
            ) : (
                <Image
                    src={src}
                    alt={postId}
                    onError={() => setErrorImage(true)}
                    fill
                    className="h-full w-full object-cover"
                    sizes="(max-width: 350px) 100vw,
              33vw"
                />
            )}
        </span>
    );
};

Comment.Img = Img;

Img.propTypes = {
    postId: PropTypes.string,
    media: PropTypes.string
};

/**
 * PostStatus
 */

const PostStatus = ({ comment, displayPost, socialNetworkType }) =>
    displayPost && socialNetworkType === FACEBOOK && comment?.media?.caption ? (
        <p className="mb-2 text-sm italic text-icoGray-500 dark:text-icoDarkMode-wolf">{`« ${
            comment?.media?.caption?.length > 30
                ? `${comment?.media?.caption?.substring(0, 30)}...`
                : comment?.media?.caption
        } »`}</p>
    ) : null;

Comment.PostStatus = PostStatus;

PostStatus.propTypes = {
    comment: PropTypes.object.isRequired,
    displayPost: PropTypes.bool,
    socialNetworkType: PropTypes.string
};

export default Comment;
